import { useTranslation } from 'react-i18next';
import { Label, Toggle, Tooltip, useSnackbar } from '@c2fo/liquidity';
import { useBuyerNavigationLayoutContext } from '@/enterprise/components/BuyerNavigationLayout';
import EditInvoices from '@/enterprise/components/OfferBuilder/EditInvoices/EditInvoices';
import useEnableAutomations from '@/enterprise/components/useEnableAutomations';
import { useExcludeFutureInvoices } from '@/enterprise/data/useExcludeFutureInvoices';
import usePlayableInvoiceStats from '@/enterprise/data/usePlayableInvoiceStats';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import EligibleInvoiceActionsDropdown from '@/enterprise/features/eligibleInvoiceTable/components/EligibleInvoiceActionsDropdown';
import MiniRuleManager from '@/enterprise/features/recurringRules/components/MiniRuleManager';
import useGetRecurringRulesForTakerMarkets from '@/enterprise/features/recurringRules/utils/useGetRecurringRulesForTakerMarkets';
import { useReporting } from '@/reporting';
import getMarketType from '@/utils/getMarketType';
import useInvoiceDownloadUrl from '@/utils/useInvoiceDownloadUrl';
import useRestrictions from '@/utils/useRestrictions';
export const InvoiceRulesTooltip = ({ children }: { children: React.ReactNode }) => {
  const { t } = useTranslation();

  return (
    <Tooltip>
      <Tooltip.Trigger asChild>{children}</Tooltip.Trigger>
      <Tooltip.Content
        arrow
        className="max-w-64 whitespace-pre-wrap text-center text-sm font-normal normal-case"
        side="left"
        variant="dark"
      >
        {t('automation.invoiceRulesTooltip')}
      </Tooltip.Content>
    </Tooltip>
  );
};

const EligibleInvoices = ({ takerMarket }: { takerMarket: TakerMarket }) => {
  const { t } = useTranslation();
  const {
    urlParams: { marketId, takerId },
  } = useBuyerNavigationLayoutContext();
  const showSnackbar = useSnackbar();
  const { trackEnterpriseEvent } = useReporting();
  const enableAutomations = useEnableAutomations();
  const { getRestrictions } = useRestrictions();
  const { canEditInvoices } = getRestrictions([takerMarket]);
  const { getRecurringRulesForTakerMarkets } = useGetRecurringRulesForTakerMarkets();
  const recurringRule = getRecurringRulesForTakerMarkets([takerMarket])[0];
  const hasRecurringRule = !!recurringRule;
  const invoiceDownloadUrl = useInvoiceDownloadUrl({ marketId, takerId, type: 'eligible' });
  const { data: playableInvoiceStats } = usePlayableInvoiceStats({
    makerOrganizationUuid: takerMarket.makerOrganizationUuid,
    takerMarkets: [takerMarket],
  });

  const { mutateAsync: toggleExcludeFutureInvoicesSea, isPending: isExcludingFutureInvoicesSea } =
    useExcludeFutureInvoices();

  const onExcludeAllFutureInvoicesClick = async () => {
    return await toggleExcludeFutureInvoicesSea(
      {
        makerOrganizationUuid: takerMarket.makerOrganizationUuid,
        offerUuid: takerMarket.offerConfig.uuid!,
        marketUuid: takerMarket.marketUuid,
        supplierDivisionUuid: takerMarket.takerDivisionUuid,
        takerMarketGroupType: getMarketType(takerMarket),
      },
      {
        onSuccess: () => {
          showSnackbar({
            message: t('offer.invoicesUpdated'),
          });
          trackEnterpriseEvent('invoice-exclude-future::submitted');
        },
        onError: () => showSnackbar({ message: t('offer.invoicesUpdateError') }),
      }
    );
  };

  const excludeFutureInvoicesLoading = isExcludingFutureInvoicesSea;
  // Renewal offer schedule for preferred could still allow excluding future invoices if account setting allows
  // Preferred Offer invoice editing is checked in canEditInvoices
  const hasOfferSchedule = !!takerMarket.offerSchedule && takerMarket.offerSchedule.type !== 'renewal';
  const excludeFutureInvoicesDisabled =
    !canEditInvoices ||
    !takerMarket.offerConfig.uuid ||
    !takerMarket.offerConfig.isEnabled ||
    takerMarket.offerConfig.frequency === 'ONE_TIME' ||
    hasOfferSchedule ||
    excludeFutureInvoicesLoading;

  return (
    <div className="px-4 py-6">
      {enableAutomations && hasRecurringRule && (
        <div className="pb-6">
          <MiniRuleManager
            referrer="divisionDetails"
            takerMarketWithRule={{
              rule: recurringRule,
              takerMarket,
            }}
          />
        </div>
      )}
      <div className="flex items-center justify-end gap-10 pb-6">
        <div className="flex items-center gap-6">
          {enableAutomations ? (
            <div className="flex items-center gap-3">
              <Toggle
                id="excludeFutureInvoices"
                accessibilityLabel={t('taker.excludeFutureInvoices')}
                checked={takerMarket.offerConfig.exclusionSettings?.excludeNewInvoices ?? false}
                className="shrink-0"
                disabled={excludeFutureInvoicesDisabled}
                loading={excludeFutureInvoicesLoading}
                onCheckedChange={onExcludeAllFutureInvoicesClick}
                size="sm"
              />
              <Label htmlFor="excludeFutureInvoices" size="sm">
                {t('taker.excludeFutureInvoices')}
              </Label>
            </div>
          ) : null}
          <div className="shrink-0">
            <EligibleInvoiceActionsDropdown
              invoiceDownloadUrl={invoiceDownloadUrl}
              // need to check new playable invoice stats since if enable invoice edits is on the old eligible invoice api will return undefined data
              invoiceDownloadUrlDisabled={(playableInvoiceStats?.totalCount ?? 0) === 0}
              takerMarket={takerMarket}
            />
          </div>
        </div>
      </div>
      <EditInvoices
        key={`${takerMarket.makerOrganizationUuid}-${takerMarket.marketUuid}-${takerMarket.takerDivisionId}`}
        asModal={false}
        referrer="division-details"
        takerMarket={takerMarket}
      />
    </div>
  );
};

export default EligibleInvoices;
